// data-fit LLC · DecisionFlow · EXECUTE Stage
// FXRateManager.jsx · DDL: R20 · API: v1.27 · S247 Phase 10
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: comp_admin full access; finance read-only; is_locked immutable;
//   rate_approved_by + rate_approved_at both required; envelope: { exchange_rates: [...], count }
// Closes: OI-S206-11
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*
// S247 Phase 10:
//   - OI-AMENDMENT-UI-BEGIN-MODAL: fires before Approve only (not before New Rate)
//   - OI-AMENDMENT-UI-COMMIT-ON-SAVE: amendment committed after successful approve
//   - New Rate (POST /exchange-rates) does NOT require an amendment — no entity exists yet
//   - Approve (POST /exchange-rates/{id}/approve) DOES require amendment — entity exists
//   - governed_entity_type = "exchange_rate"
//   - amended_entity_id = exchange_rate_id UUID passed to modal

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import AmendmentBeginModal from '../admin/AmendmentBeginModal.jsx'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

const API_BASE = '/api/v1'
const PERIOD_TYPES = ['monthly', 'weekly', 'daily']
const RATE_SOURCES = ['manual', 'ecb', 'client_internal']
const GOVERNED_ENTITY_TYPE = 'exchange_rate'

const INITIAL_FORM = { from_currency_code: '', to_currency_code: '', effective_date: '', rate: '', period_type: 'monthly', rate_source: 'manual', fiscal_period_id: '' }
const INITIAL_FILTERS = { from_currency: '', to_currency: '', is_locked: '' }

function fmt(val) { if (val === null || val === undefined) return '—'; return String(val) }
function fmtRate(val) { if (val === null || val === undefined) return '—'; return Number(val).toFixed(6) }
function fmtDate(val) { if (!val) return '—'; return String(val).slice(0, 10) }
function fmtDateTime(val) { if (!val) return '—'; try { return new Date(val).toLocaleString() } catch { return String(val) } }
function getRows(payload) { if (Array.isArray(payload?.exchange_rates)) return payload.exchange_rates; if (Array.isArray(payload?.data)) return payload.data; if (Array.isArray(payload)) return payload; return [] }
function getErrorMessage(payload) {
  const msg = payload?.error?.message || payload?.message
  if (msg) return msg
  const detail = payload?.detail
  if (Array.isArray(detail)) return detail.map(d => d.msg || String(d)).join('; ')
  if (typeof detail === 'string') return detail
  return 'Unexpected error.'
}

function StatusBadge({ rate }) {
  if (rate.is_locked) return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">🔒 Locked</span>
  if (rate.rate_approved_by) return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-success))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-success))]">✓ Approved</span>
  return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">⏳ Pending</span>
}

function SourceBadge({ source }) {
  const map = {
    manual:          'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
    ecb:             'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]',
    client_internal: 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
  }
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${map[source] || 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'}`}>{source}</span>
}

// ---------------------------------------------------------------------------
// commitAmendment — called after successful entity save
// ---------------------------------------------------------------------------
async function commitAmendment(amendmentId) {
  if (!amendmentId) return
  try {
    const res = await fetch(`${API_BASE}/amendments/${amendmentId}/commit`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      console.warn('[Amendment] Commit failed:', body?.error?.message ?? res.status)
    }
  } catch (e) {
    console.warn('[Amendment] Commit network error:', e.message)
  }
}

// ---------------------------------------------------------------------------
// CreateForm — NOT amendment-governed (new entity, no before_state possible)
// ---------------------------------------------------------------------------
function CreateForm({ onCreated, onCancel }) {
  const [form, setForm]      = useState(INITIAL_FORM)
  const [submitting, setSub] = useState(false)
  const [error, setError]    = useState(null)
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }))

  const handleSubmit = async () => {
    setError(null)
    if (!form.from_currency_code.trim() || !form.to_currency_code.trim()) { setError('from_currency_code and to_currency_code are required.'); return }
    if (form.from_currency_code.trim().toUpperCase() === form.to_currency_code.trim().toUpperCase()) { setError('from_currency_code and to_currency_code must differ.'); return }
    if (!form.effective_date) { setError('effective_date is required.'); return }
    if (!form.rate || Number(form.rate) <= 0) { setError('rate must be a positive number.'); return }
    const body = {
      from_currency_code: form.from_currency_code.trim().toUpperCase(),
      to_currency_code:   form.to_currency_code.trim().toUpperCase(),
      effective_date:     form.effective_date,
      rate:               form.rate,
      period_type:        form.period_type,
      rate_source:        form.rate_source,
    }
    if (form.fiscal_period_id !== '' && !Number.isNaN(Number(form.fiscal_period_id))) body.fiscal_period_id = Number(form.fiscal_period_id)
    setSub(true)
    try {
      const res = await fetch(`${API_BASE}/exchange-rates`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }
      onCreated(payload?.exchange_rate || payload?.data || payload)
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setSub(false)
    }
  }

  const inputClass = 'w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm'
  const labelClass = 'flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]'

  return (
    <div className="rounded-xl border border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-primary))]/5 p-4 flex flex-col gap-4">
      <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">New Exchange Rate</h3>
      {error && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className={labelClass}>From Currency *<input type="text" maxLength={3} placeholder="USD" value={form.from_currency_code} onChange={(e) => set('from_currency_code', e.target.value)} className={inputClass} /></label>
        <label className={labelClass}>To Currency *<input type="text" maxLength={3} placeholder="EUR" value={form.to_currency_code} onChange={(e) => set('to_currency_code', e.target.value)} className={inputClass} /></label>
        <label className={labelClass}>Effective Date *<input type="date" value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} className={inputClass} /></label>
        <label className={labelClass}>Rate *<input type="number" step="0.000001" min="0.000001" placeholder="1.234567" value={form.rate} onChange={(e) => set('rate', e.target.value)} className={inputClass} /></label>
        <label className={labelClass}>Period Type<select value={form.period_type} onChange={(e) => set('period_type', e.target.value)} className={inputClass}>{PERIOD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
        <label className={labelClass}>Rate Source<select value={form.rate_source} onChange={(e) => set('rate_source', e.target.value)} className={inputClass}>{RATE_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
        <label className={labelClass}>Fiscal Period ID<input type="number" step="1" placeholder="optional" value={form.fiscal_period_id} onChange={(e) => set('fiscal_period_id', e.target.value)} className={inputClass} /></label>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={handleSubmit} disabled={submitting} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">{submitting ? 'Creating…' : 'Create Rate'}</button>
        <button type="button" onClick={onCancel} disabled={submitting} className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">Cancel</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// FXRateManager
// ---------------------------------------------------------------------------
export default function FXRateManager({ fiscalPeriodId, fiscalPeriodLabel, wizardSessionId }) {
  const { user } = useAuth()
  const isCompAdmin = user?.user_type === 'comp_admin'

  const [rates, setRates]               = useState([])
  const [count, setCount]               = useState(0)
  const [filters, setFilters]           = useState(INITIAL_FILTERS)
  const [loading, setLoading]           = useState(false)
  const [error, setError]               = useState(null)
  const [showCreate, setShowCreate]     = useState(false)
  const [approvingId, setApprovingId]   = useState(null)
  const [approveError, setApproveError] = useState(null)
  const [successMsg, setSuccessMsg]     = useState(null)

  // Amendment state — only used for Approve flow
  const [amendModalOpen, setAmendModalOpen] = useState(false)
  const [amendmentId, setAmendmentId]       = useState(null)
  const [amendEntityId, setAmendEntityId]   = useState(null)  // exchange_rate_id UUID
  const [pendingApproveId, setPendingApproveId] = useState(null)

  // S579 — sort
  const [sortCol, setSortCol] = useState('effective_date')
  const [sortDir, setSortDir] = useState('desc')
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
  }
  const sortedRates = useMemo(() => {
    return [...rates].sort((a, b) => {
      let av = '', bv = ''
      if (sortCol === 'pair')            { av = `${a.from_currency_code}${a.to_currency_code}`; bv = `${b.from_currency_code}${b.to_currency_code}` }
      else if (sortCol === 'rate')       { av = Number(a.rate ?? 0); bv = Number(b.rate ?? 0) }
      else if (sortCol === 'effective_date') { av = a.effective_date ?? ''; bv = b.effective_date ?? '' }
      else if (sortCol === 'period_type')    { av = a.period_type ?? ''; bv = b.period_type ?? '' }
      else if (sortCol === 'rate_source')    { av = a.rate_source ?? ''; bv = b.rate_source ?? '' }
      else if (sortCol === 'status')         { av = a.is_locked ? '2' : a.rate_approved_by ? '1' : '0'; bv = b.is_locked ? '2' : b.rate_approved_by ? '1' : '0' }
      else if (sortCol === 'approved_at')    { av = a.rate_approved_at ?? ''; bv = b.rate_approved_at ?? '' }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [rates, sortCol, sortDir])

  const loadRates = useCallback(async (activeFilters = filters) => {
    setLoading(true); setError(null)
    const params = new URLSearchParams()
    if (activeFilters.from_currency) params.set('from_currency', activeFilters.from_currency.toUpperCase())
    if (activeFilters.to_currency)   params.set('to_currency',   activeFilters.to_currency.toUpperCase())
    if (activeFilters.is_locked !== '') params.set('is_locked', activeFilters.is_locked)
    if (fiscalPeriodId) params.set('fiscal_period_id', String(fiscalPeriodId))
    params.set('limit', '100')
    try {
      const res = await fetch(`${API_BASE}/exchange-rates?${params.toString()}`, { credentials: 'include' })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }
      setRates(getRows(payload))
      setCount(payload?.count ?? getRows(payload).length)
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setLoading(false)
    }
  }, [filters, fiscalPeriodId])

  useEffect(() => { loadRates() }, [loadRates])

  // ---------------------------------------------------------------------------
  // Approve — GOVERNED: opens amendment modal with entity ID
  // ---------------------------------------------------------------------------
  const handleApproveClick = (exchangeRateId) => {
    if (!isCompAdmin) return
    // If amendment already open for this same entity, proceed directly
    if (amendmentId && amendEntityId === String(exchangeRateId)) {
      runApprove(exchangeRateId, amendmentId)
      return
    }
    setPendingApproveId(exchangeRateId)
    setAmendEntityId(String(exchangeRateId))
    setAmendModalOpen(true)
  }

  const runApprove = async (exchangeRateId, amdId) => {
    setApprovingId(exchangeRateId); setApproveError(null); setSuccessMsg(null)
    try {
      const body = {}
      if (amdId) body.amendment_id = amdId
      if (fiscalPeriodId) body.fiscal_period_id = Number(fiscalPeriodId)
      const res = await fetch(`${API_BASE}/exchange-rates/${exchangeRateId}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setApproveError(getErrorMessage(payload)); return }
      // Commit-on-Save
      if (amdId) { await commitAmendment(amdId); setAmendmentId(null); setAmendEntityId(null) }
      setSuccessMsg('Rate approved successfully.')
      const approved = payload?.exchange_rate || payload?.data || payload
      setRates((prev) => prev.map((r) => r.exchange_rate_id === exchangeRateId ? { ...r, ...approved } : r))
    } catch (e) {
      setApproveError(e.message || 'Network error.')
    } finally {
      setApprovingId(null)
    }
  }

  // Amendment modal success handler
  const handleAmendmentBegin = (newAmendmentId) => {
    setAmendmentId(newAmendmentId)
    setAmendModalOpen(false)
    if (pendingApproveId) {
      runApprove(pendingApproveId, newAmendmentId)
      setPendingApproveId(null)
    }
  }

  const handleAmendModalCancel = () => {
    setAmendModalOpen(false)
    setPendingApproveId(null)
    setAmendEntityId(null)
  }

  // New Rate — NOT governed (no entity exists yet, no before_state possible)
  const handleCreated = (created) => {
    setShowCreate(false)
    setSuccessMsg('Exchange rate created. Pending approval before engine use.')
    if (created?.exchange_rate_id) { setRates((prev) => [created, ...prev]); setCount((c) => c + 1) } else { loadRates() }
  }

  const handleApplyFilters = () => loadRates(filters)
  const handleClearFilters = () => { setFilters(INITIAL_FILTERS); loadRates(INITIAL_FILTERS) }
  const setFilter = (field, value) => setFilters((f) => ({ ...f, [field]: value }))

  const inputClass = 'rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm'

  return (
    <div className="flex flex-col gap-4">

      {/* Amendment Begin Modal — fires for Approve only */}
      <AmendmentBeginModal
        open={amendModalOpen}
        entityType={GOVERNED_ENTITY_TYPE}
        entityId={amendEntityId}
        fiscalPeriodId={fiscalPeriodId ? Number(fiscalPeriodId) : null}
        wizardSessionId={wizardSessionId ?? null}
        onBegin={handleAmendmentBegin}
        onCancel={handleAmendModalCancel}
      />

      <div>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">FX</p>
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">FX Rate Manager</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">
          {fiscalPeriodLabel ? `Exchange rates for ${fiscalPeriodLabel}. ` : 'Exchange rates across all periods. '}
          Rates must be approved before the engine can use them in calculations.
        </p>
      </div>

      {/* Open amendment indicator */}
      {amendmentId && (
        <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))] flex items-center justify-between">
          <span>⚠ Amendment open — rate approval is governed. Approve to commit.</span>
          <button type="button" onClick={async () => {
            try { await fetch(`${API_BASE}/amendments/${amendmentId}/cancel`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }) } catch (_) {}
            setAmendmentId(null); setAmendEntityId(null)
          }} className="ml-3 text-tok-xs underline hover:no-underline">Cancel amendment</button>
        </div>
      )}

      {successMsg && (
        <div className="rounded-lg bg-[rgb(var(--color-success))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))] flex items-center justify-between">
          {successMsg}
          <button type="button" onClick={() => setSuccessMsg(null)} className="ml-3 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}
      {approveError && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))] flex items-center justify-between">
          {approveError}
          <button type="button" onClick={() => setApproveError(null)} className="ml-3 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {showCreate && isCompAdmin && <CreateForm onCreated={handleCreated} onCancel={() => setShowCreate(false)} />}

      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            Exchange Rates {!loading && <span className="font-normal text-[rgb(var(--color-text-muted))]">({count})</span>}
          </h3>
          {isCompAdmin && !showCreate && (
            <button type="button" onClick={() => { setShowCreate(true); setSuccessMsg(null) }} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))]">+ New Rate</button>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">From<input type="text" maxLength={3} placeholder="USD" value={filters.from_currency} onChange={(e) => setFilter('from_currency', e.target.value)} className={`${inputClass} w-20`} /></label>
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">To<input type="text" maxLength={3} placeholder="EUR" value={filters.to_currency} onChange={(e) => setFilter('to_currency', e.target.value)} className={`${inputClass} w-20`} /></label>
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status
            <select value={filters.is_locked} onChange={(e) => setFilter('is_locked', e.target.value)} className={`${inputClass} w-32`}>
              <option value="">All</option>
              <option value="false">Pending / Approved</option>
              <option value="true">Locked</option>
            </select>
          </label>
          <div className="flex gap-2 pb-0.5">
            <button type="button" onClick={handleApplyFilters} disabled={loading} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">Apply</button>
            <button type="button" onClick={handleClearFilters} disabled={loading} className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">Clear</button>
          </div>
        </div>

        {error && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>}

        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                {[['pair','Pair'],['rate','Rate'],['effective_date','Effective Date'],['period_type','Type'],['rate_source','Source'],['status','Status'],['approved_at','Approved At']].map(([col, label]) => (
                  <th key={col} className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]" onClick={() => toggleSort(col)}>
                    {label}<SortIcon col={col} />
                  </th>
                ))}
                {isCompAdmin && <th className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={isCompAdmin ? 8 : 7} className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading exchange rates…</td></tr>}
              {!loading && sortedRates.length === 0 && <tr><td colSpan={isCompAdmin ? 8 : 7} className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">No exchange rates found.</td></tr>}
              {!loading && sortedRates.map((rate) => {
                const canApprove = isCompAdmin && !rate.rate_approved_by && !rate.is_locked
                const isApproving = approvingId === rate.exchange_rate_id
                return (
                  <tr key={rate.exchange_rate_id} className="border-t border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className="px-3 py-2.5 text-tok-sm font-medium text-[rgb(var(--color-text))] whitespace-nowrap">{fmt(rate.from_currency_code).trim()} → {fmt(rate.to_currency_code).trim()}</td>
                    <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] font-mono whitespace-nowrap">{fmtRate(rate.rate)}</td>
                    <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap">{fmtDate(rate.effective_date)}</td>
                    <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap">{fmt(rate.period_type)}</td>
                    <td className="px-3 py-2.5 text-[rgb(var(--color-text))]"><SourceBadge source={rate.rate_source} /></td>
                    <td className="px-3 py-2.5 text-[rgb(var(--color-text))]"><StatusBadge rate={rate} /></td>
                    <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text-muted))] whitespace-nowrap">{fmtDateTime(rate.rate_approved_at)}</td>
                    {isCompAdmin && (
                      <td className="px-3 py-2.5 text-[rgb(var(--color-text))]">
                        {canApprove ? (
                          <button type="button" onClick={() => handleApproveClick(rate.exchange_rate_id)} disabled={isApproving} className="rounded-lg bg-[rgb(var(--color-success))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-success))]/80 disabled:opacity-50">{isApproving ? 'Approving…' : 'Approve'}</button>
                        ) : (
                          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{rate.is_locked ? 'Locked' : 'Approved'}</span>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
